import React from "react";
import {
  Icon,
  Row,
  Col,
  Select,
  Button,
  Checkbox,
  Input,
  Slider,
  Switch,
  InputNumber,
  Tooltip,
} from "antd";
import { poiCategories } from "../../../../services/utils";

import Colors from '../../../../themes/Colors';
const { Option } = Select;

// Card-based refine-filters layout for the map's table view (LATIN-2537).
// Purely presentational: all state and handlers live in RefinateFiltersForm.
const styles = {
  inputRadius: { borderRadius: "4px" },
  suffixText: { color: Colors.textMuted, fontSize: "12px" },
  sliderTrack: { backgroundColor: "#1890ff" },
  sliderHandle: { borderColor: "#1890ff", backgroundColor: "#1890ff" },
};

const renderCardTitle = (title) => (
  <div className="filterCardTitle">
    <span>{title}</span>
  </div>
);

const renderFieldLabel = (text) => (
  <span className="filterFieldLabel">{text}</span>
);

// Two cards per row only from 1200px up. Below that the sidebar is a narrow
// column (a third of the screen from 576px) and halves of it squeeze prices,
// sizes and durations; on phones it spans the screen with one card per row.
const cardSpan = { xs: 24, xl: 12 };

function RefinateFiltersTableView({
  t,
  search,
  onSearchChange,
  currency,
  programmatic,
  priceSliderMax,
  displaysMapMediaOwners,
  commonResolutions,
  typeOptions,
  subTypeOptions,
  localLocationType,
  localLocationSubType,
  localSizeType,
  localIsCircuit,
  resolutionSelect,
  customResolution,
  resolutionTolerance,
  enableAspectRatio,
  durationRange,
  priceMin,
  priceMax,
  selectedFormats,
  selectedMediaCompanies,
  selectedPois,
  onTypeChange,
  onSubTypeChange,
  onSizeChange,
  onCircuitChange,
  onResolutionSelect,
  onCustomResolution,
  onResolutionTolerance,
  onAspectRatioChange,
  onFormatChange,
  onDurationChange,
  onPriceChange,
  onMediaCompanyChange,
  onPoiToggle,
  onPriceSliderChange,
  onClear,
}) {
  const isSmall = localSizeType.includes("small");
  const isMedium = localSizeType.includes("medium");
  const isBig = localSizeType.includes("big");
  const isGiant = localSizeType.includes("giant");

  const currencySuffix = (
    <span style={styles.suffixText}>
      {currency ? currency.toLowerCase() : "usd"}
    </span>
  );
  const timeSuffix = (
    <span style={styles.suffixText}>
      {t("seconds") ? t("seconds").substring(0, 3) : "seg"}
    </span>
  );

  const isTraditional = programmatic === "traditional";

  const pMin = parseFloat(priceMin) || 0;
  const pMax = parseFloat(priceMax) || priceSliderMax;
  const sliderPriceValue = [pMin, Math.min(pMax, priceSliderMax)];

  return (
    <React.Fragment>
      <div className="filtersStack">
      {/* Search by name / place / region */}
      <div className="filterCard">
        {renderCardTitle(t("By name"))}
        <Input
          prefix={<Icon type="search" style={{ color: Colors.textMuted }} />}
          placeholder={t("Search")}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          style={styles.inputRadius}
        />
      </div>

      {/* Row 1: Price range · Screens */}
      <Row gutter={10} type="flex">
        <Col {...cardSpan}>
          <div className="filterCard">
            {renderCardTitle(
              <React.Fragment>
                {t("Price range")}{" "}
                <span style={{ color: "#bfbfbf", fontWeight: 400 }}>
                  ({isTraditional ? t("Per day") : "CPM"})
                </span>
              </React.Fragment>,
            )}
            <Row gutter={10}>
              <Col span={12}>
                {renderFieldLabel(t("From"))}
                <Input
                  placeholder="0"
                  value={priceMin}
                  onChange={(e) => onPriceChange("priceMin", e.target.value)}
                  suffix={currencySuffix}
                  style={styles.inputRadius}
                />
              </Col>
              <Col span={12}>
                {renderFieldLabel(t("To"))}
                <Input
                  placeholder={t("No limits applied")}
                  value={priceMax}
                  onChange={(e) => onPriceChange("priceMax", e.target.value)}
                  suffix={currencySuffix}
                  style={styles.inputRadius}
                />
              </Col>
            </Row>
            <div style={{ padding: "0 5px" }}>
              <Slider
                range
                min={0}
                max={priceSliderMax}
                value={sliderPriceValue}
                onChange={onPriceSliderChange}
                trackStyle={[styles.sliderTrack]}
                handleStyle={[styles.sliderHandle, styles.sliderHandle]}
              />
            </div>
          </div>
        </Col>

        <Col {...cardSpan}>
          <div className="filterCard">
            {renderCardTitle(t("Screens"))}
            <div className="filterFieldGroup">
              {renderFieldLabel(t("Type"))}
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder={t("Select")}
                onChange={onTypeChange}
                value={[...localLocationType]}
                maxTagCount={1}
                dropdownStyle={{ borderRadius: "6px" }}
              >
                {typeOptions}
              </Select>
            </div>
            <div className="filterFieldGroup">
              {renderFieldLabel(t("Subtype"))}
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder={t("Select")}
                onChange={onSubTypeChange}
                value={[...localLocationSubType]}
                maxTagCount={1}
                dropdownStyle={{ borderRadius: "6px" }}
              >
                {subTypeOptions}
              </Select>
            </div>
          </div>
        </Col>
      </Row>

      {/* Row 2: Resolutions · Ad format */}
      <Row gutter={10} type="flex">
        <Col {...cardSpan}>
          <div className="filterCard">
            {renderCardTitle(t("Resolutions"))}
            <Select
              mode="tags"
              style={{ width: "100%", marginBottom: 10 }}
              placeholder={t("Select")}
              optionFilterProp="children"
              value={resolutionSelect}
              onChange={onResolutionSelect}
              dropdownStyle={{ borderRadius: "6px" }}
            >
              {commonResolutions.map((res) => (
                <Option key={`${res.w}x${res.h}`} value={`${res.w}x${res.h}`}>
                  {res.w}x{res.h}
                </Option>
              ))}
            </Select>

            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              <div>
                {renderFieldLabel(t("Other resolution"))}
                <div style={{ position: "relative", width: "100%" }}>
                  <Input
                    placeholder="123x123, 456x456"
                    value={customResolution}
                    onChange={onCustomResolution}
                    style={{ ...styles.inputRadius }}
                  />
                  <Tooltip title={t("Resolution tooltip")}>
                    <Icon
                      type="info-circle"
                      style={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: Colors.textMuted,
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                </div>
              </div>

              {/* The card is half a sidebar column wide: let Aspect ratio drop
                  below Tolerance instead of overflowing the card. */}
              <div style={{ display: "flex", flexWrap: "wrap", gap: "12px 20px" }}>
                <div style={{ display: "flex", flexDirection: "column", flex: "1 1 90px", minWidth: 0, maxWidth: 140 }}>
                  {renderFieldLabel(t("Tolerance"))}
                  <Tooltip title={t("Tolerance tooltip")}>
                    <InputNumber
                      min={0}
                      max={50}
                      value={resolutionTolerance}
                      onChange={onResolutionTolerance}
                      formatter={(value) => `${value}%`}
                      parser={(value) => value.replace("%", "")}
                      style={{ width: "100%" }}
                    />
                  </Tooltip>
                </div>

                <div style={{ display: "flex", flexDirection: "column", whiteSpace: "nowrap" }}>
                  {renderFieldLabel(t("Aspect ratio"))}
                  <Tooltip title={t("Aspect ratio tooltip")}>
                    <Checkbox
                      checked={enableAspectRatio}
                      onChange={onAspectRatioChange}
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </Col>

        <Col {...cardSpan}>
          <div className="filterCard">
            {renderCardTitle(t("Ad format"))}
            <Checkbox.Group
              className="adFormatGroup"
              value={selectedFormats}
              onChange={onFormatChange}
            >
              <Checkbox value="image">{t("Image")}</Checkbox>
              <Checkbox value="video">{t("Video")}</Checkbox>
              <Checkbox value="html">{t("HTML")}</Checkbox>
            </Checkbox.Group>
          </div>
        </Col>
      </Row>

      {/* Row 3: Size · Time range */}
      <Row gutter={10} type="flex">
        <Col {...cardSpan}>
          <div className="filterCard">
            {renderCardTitle(t("Size"))}
            <Row>
              <Col span={12} style={{ marginBottom: 8 }}>
                <Checkbox checked={isSmall} onChange={() => onSizeChange("small")}>
                  {t("Small")}
                </Checkbox>
              </Col>
              <Col span={12} style={{ marginBottom: 8 }}>
                <Checkbox
                  checked={isMedium}
                  onChange={() => onSizeChange("medium")}
                >
                  {t("Medium")}
                </Checkbox>
              </Col>
              <Col span={12}>
                <Checkbox checked={isBig} onChange={() => onSizeChange("big")}>
                  {t("Large")}
                </Checkbox>
              </Col>
              <Col span={12}>
                <Checkbox checked={isGiant} onChange={() => onSizeChange("giant")}>
                  {t("Giant")}
                </Checkbox>
              </Col>
            </Row>
          </div>
        </Col>

        <Col {...cardSpan}>
          <div className="filterCard">
            {renderCardTitle(t("Ad duration range"))}
            <Row gutter={16} style={{ marginBottom: 5 }}>
              <Col span={12}>
                {renderFieldLabel(t("From"))}
                <Input
                  type="number"
                  value={durationRange[0]}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    onDurationChange([val, durationRange[1]]);
                  }}
                  suffix={timeSuffix}
                  style={styles.inputRadius}
                />
              </Col>
              <Col span={12}>
                {renderFieldLabel(t("To"))}
                <Input
                  type="number"
                  value={durationRange[1]}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 0;
                    onDurationChange([durationRange[0], val]);
                  }}
                  suffix={timeSuffix}
                  style={styles.inputRadius}
                />
              </Col>
            </Row>
            <Slider
              range
              min={5}
              max={50}
              value={[
                Math.max(5, Math.min(50, durationRange[0])),
                Math.max(5, Math.min(50, durationRange[1])),
              ]}
              onChange={onDurationChange}
              trackStyle={[styles.sliderTrack]}
              handleStyle={[styles.sliderHandle, styles.sliderHandle]}
            />
          </div>
        </Col>
      </Row>

      {/* Media owners (kept from previous form) */}
      <div className="filterCard">
        {renderCardTitle(t("Media owners"))}
        <Select
          mode="multiple"
          style={{ width: "100%" }}
          placeholder={t("Search")}
          onChange={onMediaCompanyChange}
          value={selectedMediaCompanies}
          dropdownStyle={{ borderRadius: "6px" }}
          optionFilterProp="children"
          filterOption={(input, option) =>
            String(option.props.children)
              .toLowerCase()
              .includes(input.toLowerCase())
          }
        >
          {displaysMapMediaOwners &&
            displaysMapMediaOwners.map((owner) => (
              <Option key={owner.id} value={owner.id}>
                {owner.name}
              </Option>
            ))}
        </Select>
      </div>

      {/* Points of interest */}
      <div className="filterCard">
        {renderCardTitle(t("Points of interest"))}
        <div className="poiChips">
          {poiCategories.map((cat) => {
            const selected = selectedPois.includes(cat.slug);
            return (
              <span
                key={cat.slug}
                className={`poiChip${selected ? " selected" : ""}`}
                onClick={() => onPoiToggle(cat.slug)}
              >
                {t(cat.name)}
                <Icon type={selected ? "check" : "plus"} />
              </span>
            );
          })}
        </div>
      </div>

      {/* Circuit */}
      <div className="filterCard">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <span className="filterCardTitle" style={{ marginBottom: 0 }}>
            {t("Circuit")}
          </span>
          <Switch checked={localIsCircuit} onChange={onCircuitChange} />
        </div>
      </div>
      </div>

      <div className="footerActions">
        <Button
          onClick={onClear}
          style={{
            borderRadius: "6px",
            border: `1px solid ${Colors.borderStrong}`,
            color: Colors.textSecondary,
          }}
        >
          {t("Clear filters")}
        </Button>
      </div>
    </React.Fragment>
  );
}

export default RefinateFiltersTableView;
